import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { authApi } from '@/api/authApi';
import { authSessionExpiredEvent, authTokenUtils } from '@/api/httpClient';
import { AuthContext, type AuthContextValue } from '@/context/AuthContextValue';
import type { LoginRequest, User } from '@/types/auth';

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleSessionExpired = () => {
      authTokenUtils.tokenStorage.clearTokens();
      setUser(null);
      setIsLoading(false);

      const [, lng = 'es', route = ''] = window.location.pathname.split('/');

      if (route !== 'login') {
        window.location.replace(`/${lng || 'es'}/login`);
      }
    };

    window.addEventListener(authSessionExpiredEvent, handleSessionExpired);

    return () => window.removeEventListener(authSessionExpiredEvent, handleSessionExpired);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const tokens = authTokenUtils.tokenStorage.getTokens();

      if (!tokens) {
        setIsLoading(false);
        return;
      }

      try {
        const currentUser = await authApi.me();

        if (isMounted) {
          setUser(currentUser);
        }
      } catch {
        authTokenUtils.tokenStorage.clearTokens();

        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login(credentials);
      authTokenUtils.tokenStorage.setTokens({
        accessToken: response.access_token,
        refreshToken: response.refresh_token
      });
      setUser(response.user ?? (await authApi.me()));
    } catch (loginError) {
      authTokenUtils.tokenStorage.clearTokens();
      setUser(null);
      setError(loginError instanceof Error ? loginError.message : 'Login failed');
      throw loginError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (authTokenUtils.tokenStorage.getRefreshToken()) {
        await authApi.logout();
      }
    } catch {
      // Client-side session cleanup must still happen if backend logout fails or the token expired.
    } finally {
      authTokenUtils.tokenStorage.clearTokens();
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      error,
      login,
      logout,
      clearError: () => setError(null)
    }),
    [error, isLoading, login, logout, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
